// static/table_engine_src/core/wiring.js

/**
 * Installs:
 *   ctx.wireFormatting()
 *   ctx.wireActions(actionHandlers)
 *   ctx.wireSelectionGlue({ rowMenuEnabled, ROW_HANDLE_KEY })
 *
 * NOTE:
 * - This is the “glue” that was bloating engine.js.
 * - Uses tr.dataset.teRowIdx for O(1) row index lookup.
 */
export function installWiring(ctx, { onEdit, onAction, actionHandlers } = {}) {
  if (!ctx || typeof ctx !== "object") return;

  const { table, thead, tbody, colByKey, escCss } = ctx;
  if (!table || !thead || !tbody || !colByKey) return;

  const on =
    typeof ctx.on === "function"
      ? ctx.on
      : (target, type, handler, options) => {
          if (!target || typeof target.addEventListener !== "function") return handler;
          if (typeof handler !== "function") return handler;
          target.addEventListener(type, handler, options);
          return handler;
        };

  let _formatWired = false;
  let _actionsWired = false;
  let _selectionGlueWired = false;

  function rowIndexForTr(tr) {
    const fromData = parseInt(tr?.dataset?.teRowIdx || "-1", 10);
    if (Number.isFinite(fromData) && fromData >= 0) return fromData;

    const rows = ctx.getRowEls?.() || [];
    return rows.indexOf(tr);
  }

  function wireFormatting() {
    if (_formatWired) return;
    _formatWired = true;

    on(
      table,
      "blur",
      (e) => {
        const el = e.target;
        if (!el || el.tagName !== "INPUT") return;

        const key = el.dataset.field;
        if (!key) return;

        const col = colByKey.get(key);
        if (!col || col.type !== "number") return;

        ctx.reformatNumericInput?.(el, col);
      },
      true,
    );
  }

  function wireActions() {
    if (_actionsWired) return;
    _actionsWired = true;

    on(table, "input", (e) => {
      const el = e.target;
      if (el && el.dataset && el.dataset.field) {
        const tr = el.closest?.("tr");
        const r = parseInt(tr?.dataset?.teRowIdx || "-1", 10);
        if (Number.isInteger(r) && r >= 0) ctx.markTotalsDirtyRow?.(r);
        else ctx.markTotalsDirtyAll?.();
        onEdit?.();
      }
    });

    on(table, "focusout", (e) => {
      const el = e.target;
      const key = el && el.dataset ? el.dataset.field : null;
      if (key) ctx.scheduleAutoWidth?.([key]);
    });

    on(table, "click", (e) => {
      const btn = e.target?.closest?.("button[data-action]");
      if (!btn) return;

      const action = btn.dataset.action;
      const tr = btn.closest("tr");
      if (!tr) return;

      if (action === "deleteRow") {
        const r = rowIndexForTr(tr);
        if (r < 0) return;

        // Route through row ops so minRows/notifications stay consistent.
        ctx.deleteRows?.([r]);
        return;
      }

      const fn = actionHandlers && typeof actionHandlers === "object" ? actionHandlers[action] : null;
      if (typeof fn === "function") {
        fn({ action, tr, engine: ctx.__api || null });
        onAction?.();
        ctx.updateTotals?.();
      }
    });
  }

  function wireSelectionGlue({ rowMenuEnabled, rowMenuHoverEnabled, ROW_HANDLE_KEY } = {}) {
    if (_selectionGlueWired) return;
    _selectionGlueWired = true;

    const ua =
      (typeof navigator !== "undefined" && typeof navigator.userAgent === "string")
        ? navigator.userAgent
        : "";
    const needsNativePasteProxy =
      /Safari/i.test(ua) &&
      !/(Chrome|Chromium|Edg|OPR|CriOS|FxiOS)/i.test(ua);

    const hasCellSelection = () =>
      !!ctx?._sel?.cellAnchor || !!ctx?._sel?.cellRange || !!ctx?._sel?.cellFocus;

    const isCellEditorEl = (el) =>
      !!el &&
      !!el.matches?.("input, textarea, select") &&
      table.contains(el) &&
      !!el.closest?.("tbody");

    const ensurePasteProxy = () => {
      if (!needsNativePasteProxy) return null;
      const existing = ctx._tePasteProxy;
      if (existing && document.contains(existing)) return existing;

      const proxy = document.createElement("textarea");
      proxy.className = "te-clipboard-proxy";
      proxy.setAttribute("aria-hidden", "true");
      proxy.setAttribute("tabindex", "-1");
      proxy.setAttribute("spellcheck", "false");
      proxy.setAttribute("autocomplete", "off");
      proxy.setAttribute("autocorrect", "off");
      proxy.setAttribute("autocapitalize", "off");
      proxy.setAttribute("data-te-paste-proxy", "1");
      Object.assign(proxy.style, {
        position: "fixed",
        left: "-200vw",
        top: "0",
        width: "1px",
        height: "1px",
        opacity: "0",
        pointerEvents: "none",
      });

      table.appendChild(proxy);
      ctx._tePasteProxy = proxy;
      return proxy;
    };

    const isPasteProxyEl = (el) => !!el && el === ctx._tePasteProxy;

    const primePasteProxySoon = () => {
      if (!needsNativePasteProxy) return;
      if (!hasCellSelection()) return;
      if (typeof ctx.isMenuOpen === "function" && ctx.isMenuOpen()) return;

      setTimeout(() => {
        if (!hasCellSelection()) return;

        const ae = document.activeElement;
        if (isCellEditorEl(ae)) return;
        if (ae && !table.contains(ae)) {
          const isExternalEditor = !!(ae.matches?.("input, textarea, select") || ae.isContentEditable);
          if (isExternalEditor) return;
        }

        const proxy = ensurePasteProxy();
        if (!proxy || ae === proxy) return;

        try {
          proxy.focus({ preventScroll: true });
          proxy.select?.();
        } catch {}
      }, 0);
    };

    const syncSel = () => {
      try {
        ctx.applySelectionVisuals?.();
      } catch {}
      primePasteProxySoon();
    };

    const focusTableSoon = () => {
      const run = () => {
        try {
          table.focus({ preventScroll: true });
        } catch {}
        primePasteProxySoon();
      };
      if (typeof queueMicrotask === "function") queueMicrotask(run);
      else Promise.resolve().then(run);
    };

    const isRowHandleKey = (k) => rowMenuEnabled && k === ROW_HANDLE_KEY;

    const rowIndexForTr = (tr) => {
      const r = parseInt(tr?.dataset?.teRowIdx || "-1", 10);
      return Number.isFinite(r) ? r : -1;
    };

    const isPointerNearBottomRight = (cell, evt, px = 12) => {
      if (!cell || !evt) return false;
      const rect = cell.getBoundingClientRect?.();
      if (!rect) return false;
      return evt.clientX >= rect.right - px && evt.clientY >= rect.bottom - px;
    };

    const primePasteProxyFromCellEvent = (e) => {
      const td = e?.target?.closest?.("td[data-col-idx]");
      const tr = e?.target?.closest?.("tr");
      if (!td || !tr || !tbody.contains(tr)) return;
      if (isRowHandleKey(td.dataset.colKey)) return;
      primePasteProxySoon();
    };

    const headerClickSquelched = () => Number(ctx._teSquelchHeaderClickUntil || 0) > Date.now();

    // Keep shortcuts/ESC working after header interactions
    on(thead, "click", focusTableSoon, true);
    on(thead, "contextmenu", focusTableSoon, true);
    on(tbody, "mouseup", primePasteProxyFromCellEvent, true);
    on(tbody, "touchend", primePasteProxyFromCellEvent, true);
    on(tbody, "contextmenu", primePasteProxyFromCellEvent, true);

    // Header selection + header context menu
    on(thead, "click", (e) => {
      if (headerClickSquelched()) return;
      if (e.target?.closest?.(".te-col-resizer")) return;

      const th = e.target.closest("th[data-col-key]");
      if (!th) return;

      const colKey = th.dataset.colKey;
      const col = colByKey.get(colKey);
      if (col && col.kind === "rowHandle") return;

      ctx.selectColumnByClick?.(colKey, e);
      syncSel();
    });

    on(thead, "contextmenu", (e) => {
      if (headerClickSquelched()) return;
      if (e.target?.closest?.(".te-col-resizer")) return;

      const th = e.target.closest("th[data-col-key]");
      if (!th) return;

      const colKey = th.dataset.colKey;
      const col = colByKey.get(colKey);
      if (col && col.kind === "rowHandle") return;

      e.preventDefault();

      const has = ctx._sel?.selectedCols?.has?.(colKey);
      const any = (ctx._sel?.selectedCols?.size || 0) > 0;

      if (!any || !has) {
        ctx.setSelectedColumns?.([colKey], { anchorIdx: ctx.colIndexByKey?.(colKey) ?? 0 });
        syncSel();
      }

      ctx.showMenu?.(e.clientX, e.clientY, ctx.buildHeaderMenu?.());
    });

    // Row-handle selection + row context menu (optional)
    if (rowMenuEnabled) {
      on(tbody, "click", (e) => {
        const td = e.target.closest(`td[data-col-key="${escCss(ROW_HANDLE_KEY)}"]`);
        const tr = e.target.closest("tr");
        if (!td || !tr) return;

        const r = rowIndexForTr(tr);
        if (r < 0) return;

        ctx.selectRowByClick?.(r, e);
        syncSel();
        focusTableSoon();
      });

      on(tbody, "contextmenu", (e) => {
        const td = e.target.closest(`td[data-col-key="${escCss(ROW_HANDLE_KEY)}"]`);
        const tr = e.target.closest("tr");
        if (!td || !tr) return;

        e.preventDefault();

        const r = rowIndexForTr(tr);
        if (r < 0) return;

        const has = ctx._sel?.selectedRows?.has?.(r);
        const any = (ctx._sel?.selectedRows?.size || 0) > 0;

        if (!any || !has) {
          ctx.setSelectedRows?.([r], { anchorIdx: r });
          syncSel();
        }

        ctx.showMenu?.(e.clientX, e.clientY, ctx.buildRowMenu?.(r));
        focusTableSoon();
      });
    }

    // Hover-trigger (⋮) click handlers for row handle cells (opt-in)
    if (rowMenuHoverEnabled) {
      // Body rows: click ⋮ to open full row menu
      on(tbody, "click", (e) => {
        const btn = e.target.closest(".te-row-menu-trigger");
        if (!btn) return;

        e.stopPropagation();

        const tr = e.target.closest("tr");
        if (!tr) return;

        const r = rowIndexForTr(tr);
        if (r < 0) return;

        ctx.setSelectedRows?.([r], { anchorIdx: r });
        syncSel();

        const rect = btn.getBoundingClientRect();
        ctx.showMenu?.(rect.left, rect.bottom + 2, ctx.buildRowMenu?.(r));
        focusTableSoon();
      });

      // Header: click ⋮ to insert row at end
      on(thead, "click", (e) => {
        const btn = e.target.closest(".te-row-menu-trigger");
        if (!btn) return;

        e.stopPropagation();

        const nRows = (ctx.getRowEls?.() || []).length;
        const lastIdx = Math.max(0, nRows - 1);

        const rect = btn.getBoundingClientRect();
        ctx.showMenu?.(rect.left, rect.bottom + 2, () => {
          const m = ctx.ensureMenu?.();
          if (!m) return;
          m.innerHTML = "";

          const b = document.createElement("button");
          b.type = "button";
          b.textContent = "Insert row";
          b.style.display = "block";
          b.style.width = "100%";
          b.style.textAlign = "left";
          b.style.padding = "8px 10px";
          b.style.borderRadius = "8px";
          b.style.border = "0";
          b.style.background = "transparent";
          b.style.color = "white";
          b.style.cursor = "pointer";
          b.style.fontSize = "13px";
          b.addEventListener("mouseenter", () => { b.style.background = "rgba(255,255,255,0.08)"; });
          b.addEventListener("mouseleave", () => { b.style.background = "transparent"; });
          b.addEventListener("click", () => {
            ctx.insertRowBelow?.(lastIdx);
            ctx.hideMenu?.();
          });
          m.appendChild(b);
        });
        focusTableSoon();
      });
    }

    // Cell anchor / range selection (body cells)
    let fillDrag = null;

    const restoreSelectionRange = (range) => {
      if (!range) return;
      ctx.setCellRangeFromAnchor?.(range.r1, range.c1, {
        anchor: { r: range.r0, c: range.c0 },
      });
      syncSel();
    };

    const endFillDrag = async ({ commit = true } = {}) => {
      if (!fillDrag) return;
      const state = fillDrag;
      fillDrag = null;
      table.classList.remove("te-fill-dragging");

      if (!commit) {
        restoreSelectionRange(state.originalRange);
        return;
      }

      if (Number.isInteger(state.targetRow) && state.targetRow > state.baseRange.r1) {
        if (typeof ctx.fillDownSelectionToRow === "function") {
          await ctx.fillDownSelectionToRow(state.targetRow);
        } else if (typeof ctx.fillDownFromSelection === "function") {
          await ctx.fillDownFromSelection();
        }
      } else {
        restoreSelectionRange(state.originalRange);
      }
    };

    on(
      document,
      "pointermove",
      (e) => {
        if (!fillDrag) return;
        const tr = e.target?.closest?.("tr");
        if (!tr || !tbody.contains(tr)) return;
        const r = rowIndexForTr(tr);
        if (r < fillDrag.baseRange.r1) return;

        fillDrag.targetRow = r;
        ctx.setCellRangeFromAnchor?.(r, fillDrag.baseRange.c1, {
          anchor: { r: fillDrag.baseRange.r0, c: fillDrag.baseRange.c0 },
        });
        syncSel();
      },
      true,
    );

    on(
      document,
      "pointerup",
      async () => {
        if (!fillDrag) return;
        await endFillDrag({ commit: true });
      },
      true,
    );

    on(
      document,
      "pointercancel",
      async () => {
        if (!fillDrag) return;
        await endFillDrag({ commit: false });
      },
      true,
    );

    on(
      tbody,
      "pointerdown",
      (e) => {
        const td = e.target?.closest?.("td[data-col-idx]");
        const tr = e.target?.closest?.("tr");
        if (!td || !tr || !tbody.contains(tr)) return;
        if (isRowHandleKey(td.dataset.colKey)) return;
        if (!td.dataset?.teFillHandleCell) return;
        if (!isPointerNearBottomRight(td, e, 12)) return;

        const sel = ctx.getSelection?.();
        if (!sel) return;

        fillDrag = {
          baseRange: { ...sel },
          originalRange: { ...sel },
          targetRow: sel.r1,
        };
        table.classList.add("te-fill-dragging");
        e.preventDefault();
        e.stopPropagation();
      },
      true,
    );

    on(
      tbody,
      "dblclick",
      async (e) => {
        const td = e.target?.closest?.("td[data-col-idx]");
        const tr = e.target?.closest?.("tr");
        if (!td || !tr || !tbody.contains(tr)) return;
        if (isRowHandleKey(td.dataset.colKey)) return;
        if (!td.dataset?.teFillHandleCell) return;
        if (!isPointerNearBottomRight(td, e, 12)) return;

        e.preventDefault();
        e.stopPropagation();

        const r = rowIndexForTr(tr);
        const c = parseInt(td.dataset.colIdx || "-1", 10);
        if (!(Number.isFinite(r) && r >= 0 && Number.isFinite(c) && c >= 0)) return;

        // Excel-like double-click handle behavior:
        // use focused/active cell as source and fill downward for the rest of the column.
        ctx.setCellAnchor?.(r, c);
        syncSel();

        const rows = ctx.getRowEls?.() || [];
        const targetRow = rows.length - 1;
        if (!Number.isInteger(targetRow) || targetRow <= r) return;

        if (typeof ctx.fillDownSelectionToRow === "function") {
          await ctx.fillDownSelectionToRow(targetRow);
        } else if (typeof ctx.fillDownFromSelection === "function") {
          await ctx.fillDownFromSelection();
        }
        focusTableSoon();
      },
      true,
    );

    on(tbody, "click", (e) => {
      const td = e.target.closest("td[data-col-idx]");
      const tr = e.target.closest("tr");
      if (!td || !tr) return;

      if (e.target.closest("button")) return;
      if (isRowHandleKey(td.dataset.colKey)) return;

      const r = rowIndexForTr(tr);
      const c = parseInt(td.dataset.colIdx || "0", 10);

      if (r >= 0 && Number.isFinite(c)) {
        const additive = !!(e.metaKey || e.ctrlKey);
        if (additive && e.shiftKey) {
          ctx.setCellRangeFromAnchor?.(r, c, { append: true });
        } else if (additive) {
          ctx.setCellRangeFromAnchor?.(r, c, { append: true, anchor: { r, c } });
        } else if (e.shiftKey) {
          ctx.setCellRangeFromAnchor?.(r, c);
        } else {
          ctx.setCellAnchor?.(r, c);
        }
        syncSel();
      }
    });

    // Keep TE selection in sync with real DOM focus (Tab / click)
    on(
      table,
      "focusin",
      (e) => {
        const el = e.target;
        if (!el) return;
        if (!el.matches?.("input, textarea, select")) return;
        if (el.closest?.(".te-popover, .te-context-menu, .te-menu, .te-dropdown")) return;

        const td = el.closest("td[data-col-idx]");
        const tr = el.closest("tr");
        if (!td || !tr) return;
        if (!tbody.contains(tr)) return;
        if (isRowHandleKey(td.dataset.colKey)) return;

        const r = rowIndexForTr(tr);
        const c = parseInt(td.dataset.colIdx || "0", 10);

        if (r >= 0 && Number.isFinite(c)) {
          ctx.setCellAnchor?.(r, c);
          syncSel();
        }
      },
      true,
    );

    const isEditable = (el) =>
      !!el && (el.matches?.("input, textarea, select") || !!el.isContentEditable);

    const inTableContext = (target) => {
      const ae = document.activeElement;
      if (ae && !table.contains(ae) && isEditable(ae)) return false;
      if (target && !table.contains(target) && isEditable(target)) return false;
      if (target?.closest?.(".te-popover, .te-context-menu, .te-menu, .te-dropdown")) return false;
      return table.contains(target) || table.contains(ae) || !!ctx._sel?.cellAnchor || !!ctx._sel?.cellRange;
    };

    const clipboardPayloadFromEvent = (e) => {
      const readFirst = (tries) => {
        for (const fn of tries) {
          try {
            const v = fn();
            if (typeof v === "string" && v.length) return v;
          } catch {}
        }
        return "";
      };

      const text = readFirst([
        () => e?.clipboardData?.getData?.("text/plain"),
        () => e?.clipboardData?.getData?.("text"),
        () => e?.clipboardData?.getData?.("Text"),
        () => e?.dataTransfer?.getData?.("text/plain"),
        () => e?.dataTransfer?.getData?.("text"),
      ]);
      const html = readFirst([
        () => e?.clipboardData?.getData?.("text/html"),
        () => e?.dataTransfer?.getData?.("text/html"),
      ]);
      const dataText = typeof e?.data === "string" ? e.data : "";
      return { text: text || dataText, html };
    };

    const resolveCellEditorTarget = (e) => {
      const target = e?.target || null;
      if (isCellEditorEl(target)) return target;
      const ae = document.activeElement;
      if (isCellEditorEl(ae)) return ae;
      return null;
    };

    const hasMultiCellSelection = () => {
      const rawRanges = typeof ctx.getSelections === "function" ? ctx.getSelections() : null;
      const ranges =
        (Array.isArray(rawRanges) && rawRanges.length ? rawRanges : null) ||
        (ctx?._sel?.cellRange ? [ctx._sel.cellRange] : []);

      if (!Array.isArray(ranges) || !ranges.length) return false;
      let cells = 0;
      for (const rg of ranges) {
        if (!rg) continue;
        const r0 = Math.min(Number(rg.r0), Number(rg.r1));
        const r1 = Math.max(Number(rg.r0), Number(rg.r1));
        const c0 = Math.min(Number(rg.c0), Number(rg.c1));
        const c1 = Math.max(Number(rg.c0), Number(rg.c1));
        if (![r0, r1, c0, c1].every(Number.isFinite)) continue;
        cells += Math.max(0, r1 - r0 + 1) * Math.max(0, c1 - c0 + 1);
        if (cells > 1) return true;
      }
      return false;
    };

    const handlePasteLikeEvent = async (e, explicitPayload = null) => {
      if (typeof ctx.pasteFromClipboard !== "function") return;
      if (!inTableContext(e?.target)) return;

      const payload =
        explicitPayload && typeof explicitPayload === "object"
          ? {
              text: String(explicitPayload.text || ""),
              html: String(explicitPayload.html || ""),
            }
          : clipboardPayloadFromEvent(e);
      const text = String(payload.text || "");
      const html = String(payload.html || "");
      const isTargetEditable = isEditable(e?.target);
      const multiCell = hasMultiCellSelection();
      const editorTarget = resolveCellEditorTarget(e);

      if (!text && !html) {
        // Safari native paste UI can omit ClipboardEvent payload.
        // If target is an in-table editor and we have a multi-cell range, let native
        // paste happen first, then propagate the resulting cell value across range.
        if (multiCell && editorTarget) {
          const targetEl = editorTarget;
          setTimeout(async () => {
            try {
              const fromEditor = String(targetEl?.value ?? "");
              const fallback = fromEditor || String(ctx._teLastCopiedText || "");
              if (!fallback) return;
              ctx._teLastCopiedText = fallback;
              await ctx.pasteFromClipboard({ text: fallback, html: "" });
            } catch {}
          }, 0);
          return;
        }

        // If we still have no event payload, but we have app-local copy cache,
        // use it for range paste to avoid clipboard permission failures.
        if (multiCell) {
          const cached = String(ctx._teLastCopiedText || "");
          if (cached) {
            e?.preventDefault?.();
            e?.stopPropagation?.();
            await ctx.pasteFromClipboard({ text: cached, html: "" });
            return;
          }
        }

        // For regular single-cell native paste in editors, let browser handle it.
        if (isTargetEditable && !multiCell) return;
        // If browser gave us no payload and we cannot recover, do not fire
        // clipboard API fallback (it commonly fails under Safari native UI).
        return;
      }

      e?.preventDefault?.();
      e?.stopPropagation?.();
      await ctx.pasteFromClipboard({ text, html });
    };

    // Native browser copy path (context menu / menubar copy).
    on(
      document,
      "copy",
      (e) => {
        if (typeof ctx.copySelectionToTSV !== "function") return;
        if (!inTableContext(e?.target)) return;

        const text = String(ctx.copySelectionToTSV() || "");
        const html = String(ctx.copySelectionToHtml?.() || "");
        if (!text) return;
        ctx._teLastCopiedText = text;
        ctx._teLastCopiedHtml = html;
        try {
          ctx.captureCopySourceFromSelection?.();
        } catch {}

        // Populate system clipboard when available.
        try {
          if (e?.clipboardData?.setData) {
            e.clipboardData.setData("text/plain", text);
            if (html) e.clipboardData.setData("text/html", html);
            e.preventDefault();
          }
        } catch {}
      },
      true,
    );

    // Native browser paste path (Safari context-menu "Paste", etc.).
    const pasteProxy = ensurePasteProxy();
    if (pasteProxy) {
      on(
        pasteProxy,
        "input",
        async (e) => {
          const text = String(pasteProxy.value || "");
          if (!text) return;
          pasteProxy.value = "";
          await handlePasteLikeEvent(e, { text, html: "" });
        },
        true,
      );
    }

    on(
      document,
      "paste",
      async (e) => {
        if (isPasteProxyEl(e?.target)) {
          const payload = clipboardPayloadFromEvent(e);
          if (!payload.text && !payload.html) return;
          await handlePasteLikeEvent(e, payload);
          return;
        }
        await handlePasteLikeEvent(e);
      },
      true,
    );

    // Safari may emit beforeinput for paste flows where ClipboardEvent text is missing.
    on(
      document,
      "beforeinput",
      async (e) => {
        const t = String(e?.inputType || "");
        if (t !== "insertFromPaste" && t !== "insertFromPasteAsQuotation") return;
        if (isPasteProxyEl(e?.target)) return;
        const payload = clipboardPayloadFromEvent(e);
        if (!payload.text && !payload.html) return;
        await handlePasteLikeEvent(e, payload);
      },
      true,
    );
  }

  ctx.wireFormatting = wireFormatting;
  ctx.wireActions = wireActions;
  ctx.wireSelectionGlue = wireSelectionGlue;
}
